import { ref, watch } from 'vue';

export type ThemePref = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'intranet.theme';

function read(): ThemePref {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === 'light' || v === 'dark' ? v : 'system';
}

/**
 * Выбор темы: `system` (по умолчанию) следует за ОС, `light`/`dark` — явный
 * выбор пользователя.
 *
 * Системную тему резолвим здесь, а не media-query в tokens.css: токены общие
 * для всех приложений платформы, и media-query перекрасил бы в тёмное те из
 * них, которые под неё не проверяли. Поэтому `data-theme` на <html> ставится
 * всегда — тёмная тема остаётся opt-in этого приложения.
 *
 * Состояние — модуль-синглтон: стор ради одного значения избыточен.
 */
const pref = ref<ThemePref>(read());

const media =
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;

function resolve(v: ThemePref): 'light' | 'dark' {
  if (v !== 'system') return v;
  return media?.matches ? 'dark' : 'light';
}

function apply(v: ThemePref): void {
  document.documentElement.setAttribute('data-theme', resolve(v));
}

watch(pref, (v) => {
  apply(v);
  if (v === 'system') localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, v);
});

/** Применить сохранённую тему до первого рендера (вызывать в main.ts). */
export function initTheme(): void {
  apply(pref.value);
  // Пока выбрана системная — следуем за переключением темы в ОС на лету.
  media?.addEventListener('change', () => {
    if (pref.value === 'system') apply('system');
  });
}

export function useTheme() {
  return { pref };
}
